import { restApiFP } from "@plannel/services/utils/rest-api";
import axiosUtil from "@plannel/utils/axios-bigint";

// FP 백엔드는 @RequestBody JSON이 아니라 request.getParameter("changes"/"params")로 읽는
// form-urlencoded 컨벤션을 씀(saas-fp의 RequestParameter 참고). rest-api.js의 axios 인스턴스는
// 기본 Content-Type이 application/json이라 이 요청들만 명시적으로 오버라이드해야 함.
const FORM_HEADERS = { headers: { "Content-Type": "application/x-www-form-urlencoded;charset=utf-8" } };

const toForm = (fields) => {
  const form = new URLSearchParams();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      // 그리드에서 가져온 row 데이터의 id 등은 axios-bigint의 transformResponse가 네이티브
      // BigInt로 파싱해두므로, 순수 JSON.stringify가 아니라 BigInt-safe 직렬화를 써야 함.
      form.append(key, typeof value === "string" ? value : axiosUtil.jsonbigStringfy(value));
    }
  });
  return form;
};

const postParams = (url, params) => restApiFP.post(url, toForm({ params }), FORM_HEADERS);
const postChanges = (url, changes) => restApiFP.post(url, toForm({ changes }), FORM_HEADERS);
// Simulation 계열 저장 엔드포인트는 `changes` 하나로 안 끝난다 — 예: SimulationController의
// `simulation-step/step-seqs/save`는 request.getParameter("changes")(헤더+update/create/delete 묶음)와
// request.getParameter("plan-scope")를 **각각** 읽는다(Master 저장은 행마다 planScope를 들고 가는
// List<Dto> 하나뿐이라 postChanges로 충분했다). 임의의 최상위 form 필드 조합을 보낼 때 쓴다.

// 필드명이 "changes" 하나로 고정되지 않은 엔드포인트용(예: fpconfig-save/delete가 받는
// settingChanges/comnCodeChanges 두 개의 별도 form 필드) — 필드명을 그대로 넘겨 postChanges와
// 동일한 방식(BigInt-safe 직렬화)으로 전송한다.
const postFields = (url, fields) => restApiFP.post(url, toForm(fields), FORM_HEADERS);

// 같은 form 필드 규약을 multipart/form-data 로 보내는 판.
//
// urlencoded 는 JSON 본문을 퍼센트 인코딩해서 실어 보내기 때문에 `{`, `}`, `"`, `,`, 공백이 전부
// 3바이트로 부풀어 실제 전송량이 원본의 1.5배가 된다. Tomcat 의 `maxPostSize`(기본 2MB)는
// **인코딩된 바이트**를 세므로, 1.4MB 짜리 JSON 하나가 2.1MB 로 계산돼 한도를 넘긴다. 그런데
// Tomcat 은 이때 예외를 던지지 않고 **파라미터 맵을 통째로 비운다** — 서버에선 크기와 무관해
// 보이는 `getParameter("changes") == null` → Jackson 의 `argument "content" is null` 로만 나타나서
// 원인을 찾기가 매우 어렵다(backend-gaps.md #16).
//
// multipart 는 파트 본문을 인코딩 없이 원문 그대로 싣고, Tomcat 도 파트의 raw 크기만 센다.
// old(AdjustmentGrid3.jsx)도 이 엔드포인트에 FormData 를 썼다 — 즉 원래 규약으로 되돌리는 것이지
// 새 우회가 아니다. 서버는 어느 쪽이든 `request.getParameter(name)` 으로 동일하게 읽는다.
//
// Content-Type 에 boundary 를 직접 못 쓰지만, axios 는 FormData 를 감지하면 이 헤더를 비워
// 브라우저가 boundary 를 붙이게 한다(data-load-validation-service 와 동일 패턴).
const MULTIPART_HEADERS = { headers: { "Content-Type": "multipart/form-data" } };

const toMultipart = (fields) => {
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      form.append(key, typeof value === "string" ? value : axiosUtil.jsonbigStringfy(value));
    }
  });
  return form;
};

const postMultipartFields = (url, fields) => restApiFP.post(url, toMultipart(fields), MULTIPART_HEADERS);

const fpApiUtil = { postParams, postChanges, postFields, postMultipartFields };
export default fpApiUtil;
